import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { toastSmsWarnings } from '@/lib/smsWarnings.js';
import { getPresentOptions, presentDealQuotes } from '@/services/quoteIngest.service.js';
import { clearQuoteReferral } from '@/services/quotes.service.js';
import { getQuoteFull } from '@/services/quotes.service.js';
import { missingItemDoor } from '@/lib/quoteCompleteness.js';
import QuoteWizardSheet from '@/components/sheets/QuoteWizardSheet.jsx';
import LogHangarQuoteSheet from '@/components/sheets/LogHangarQuoteSheet.jsx';
import LogGlQuoteSheet from '@/components/sheets/LogGlQuoteSheet.jsx';
import DealReopenKey from '@/components/account/DealReopenKey.jsx';
// A pilot off the deal's pilot list is fixed on the deal roster: the existing
// roster door (PUT /deals/:dealId/roster, services/roster.service.js), no new writer.
const RosterSheet = lazy(() => import('@/components/sheets/RosterSheet.jsx'));
import { optionRowLabel, deductibleLine } from '@/lib/quoteFields.js';
import { formatLiability } from '@/lib/liability.js';

// MV-03 §2.4 present sheet: every active option grouped by carrier, the
// broker CHECKS which the client sees, MAY mark one recommended with an
// optional one-line why, and picks the channel(s). Sending happens on this
// explicit action only. Tower renders the curated set + badge + the
// "other options we obtained" expandable.
//
// 2026-07-28 (why it stopped nagging):
//  - The recommend radio only renders when there are 2+ rows to choose
//    between. One option is not a choice, and Tower hides the badge and the
//    why line on a single-card present anyway — the required text went
//    nowhere. Clicking a marked radio again clears it.
//  - The why is optional and labeled as the note it is. It still goes on the
//    record when written.
//  - The text channel is only offered when there IS a number. The server
//    finds one (session, then the CRM contact); if there is none, this sheet
//    collects one inline and the send backfills it. No number, no button.

const fmt = (n) => (n == null || !Number.isFinite(Number(n)) ? 'no premium' : '$' + Number(n).toLocaleString('en-US'));

// Display-only: 10-digit US numbers read as (706) 555-0143, everything else
// (short, E.164, international) prints as stored.
const fmtPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : String(p || '');
};
const phoneIsSendable = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
};

// WHAT THE SERVER SAYS THIS ROW STILL NEEDS BEFORE PRESENT (decision 13).
//
// The list is the present gate's own (present-options present_missing, per
// option, and per quote for an envelope). This sheet derives nothing: it greys
// the box while the list is non-empty and puts each item beside it, each one
// opening its field. A row the server could not check (present_missing null) is
// held too, as 'could not check', never as ready. READY is the one value that
// means the server checked and found nothing.
//
// It replaces "Going to the client without: ...", the browser's own score of
// the row (computeQuoteCompleteness over fromPresentRow), which reported and
// never gated.
const READY = null;
function rowMissing(q, o) {
  const list = o ? o.present_missing : q.present_missing;
  if (!Array.isArray(list)) return [];
  return list.length ? list : READY;
}
function PresentMissing({ items, q, o, onOpen, lead = 'Not ready to present:' }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="mt-1 pl-7 text-[11px] text-warning" data-present-missing>
      <span className="font-medium">{list.length ? lead : 'Could not check this quote.'}</span>
      {list.map((item) => {
        const d = missingItemDoor(item, { policyType: q.policy_type || null });
        return d.kind === 'none' ? (
          <span key={item.id || item.key} className="ml-2 text-text-muted">{item.label}{d.why ? ` (${d.why})` : ''}</span>
        ) : (
          <button key={item.id || item.key} type="button" onClick={() => onOpen?.(q, o, item)} data-missing-item={item.key}
            className="ml-2 underline underline-offset-2 hover:text-text">
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

// A REFERRED RATE IS NOT A QUOTE, AND THE SHEET SAYS SO WHERE THE BOX IS.
//
// 2026-09-21, N5997P: IAT's AeroQuote reply on 09-18 was "UW Review", so the
// row was correctly stamped referred. On 09-21 David typed the firmed terms
// into that same row through the ladder's quick-log, which never asks about
// the referral, so the flag outlived the edit that firmed it. This sheet then
// could not see the flag (present-options did not project it), let him check
// the box, and answered with a 409 whose two named controls lived on another
// surface, delivered as a toast with the URL in front of the sentence.
//
// The state is on the card now, the two clearing controls sit beside it, and
// the box is held until one of them is used. Same writer as the ladder
// (POST /quotes/:id/referral/clear, attributed, logged); no second door.
// `acknowledge_referred` stays a server-only path on purpose: presenting a
// rate the carrier has not agreed is never a default, and nothing here sends
// it.
function ReferredNotice({ q, clearing, blocked, onClear }) {
  const busy = clearing === q.id;
  return (
    <div className={`rounded border px-3 py-2 text-[11.5px] ${blocked ? 'border-danger/50 bg-danger-soft text-danger' : 'border-warning/50 bg-warning-soft text-warning'}`}>
      <p>
        <span className="font-semibold">Referred.</span> The carrier has not agreed to this rate
        {q.quote_number ? ` (#${q.quote_number})` : ''}, so it cannot go to the client as it stands.
        {blocked ? ` ${blocked}` : ''}
      </p>
      <p className="mt-1 flex flex-wrap gap-x-3 text-[11px]">
        <button
          type="button"
          onClick={() => onClear(q.id, 'firmed')}
          disabled={busy}
          className="underline hover:text-success disabled:opacity-50"
          title="The carrier came back and agreed the rate. Clears the referred flag, attributed to you."
        >
          {busy ? '…' : 'Terms confirmed'}
        </button>
        <button
          type="button"
          onClick={() => onClear(q.id, 'misclick')}
          disabled={busy}
          className="text-text-muted underline hover:text-text disabled:opacity-50"
          title="It was never referred. Clears the flag without claiming the carrier firmed anything."
        >
          {busy ? '…' : 'not referred after all'}
        </button>
      </p>
    </div>
  );
}

// Hoisted (no-unstable-nested-components): one selectable option row. o=null
// renders the quote itself (envelope quotes carry no option rows). `held` is
// the referred hold: the box cannot be ticked until the referral is cleared,
// though an already-ticked one can still be unticked.
function OptionRow({ q, o, checked, onToggle, recommendedKey, onRecommend, showRecommend, held: referredHeld = false, missing = READY, onOpenItem }) {
  const key = o ? `${q.id}:${o.id}` : q.id;
  const isOn = !!checked[key];
  // Held while referred, while the server lists what it still needs, and
  // while its check could not run. An already-ticked box can still be unticked.
  const held = referredHeld || missing !== READY;
  const isRec = recommendedKey === key;
  // A junk label is dropped rather than printed. utils/quote-card.js has
  // suppressed the engine's 'Option' default on the client card since
  // 2026-07-29; this sheet was still printing it, and adding its own fallback
  // on top so that a quote with NO label showed one anyway (item 4,
  // 2026-07-30). Same rule, one helper, so the two surfaces cannot drift.
  const label = o
    ? [optionRowLabel(o.option_label, o.coverage_type), o.coverage_type, fmt(o.total_premium)].filter(Boolean).join(' · ')
    : [`${q.policy_type ? q.policy_type.replace('_', ' ') : 'Quote'}`, q.quote_number ? `#${q.quote_number}` : null].filter(Boolean).join(' · ');
  return (
    <div className="rounded border border-border bg-bg px-3 py-2">
      <div className="flex items-center gap-2.5">
      <input type="checkbox" checked={isOn} disabled={held && !isOn} onChange={(e) => onToggle(key, e.target.checked)} className="h-4 w-4 accent-accent disabled:opacity-40" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-text">{label}</p>
        {o ? (
          <p className="text-[11px] text-text-muted">
            {[o.hull_value ? `hull ${fmt(o.hull_value)}` : null,
              // THE WHOLE STRUCTURE, through the resolver the ladder uses. This
              // printed the occurrence figure alone, so a $2M CSL and a
              // $2M/$250K option read identically (Starr, 2026-09-09).
              formatLiability(o) || null,
              // Always rendered, because "not stated" is itself the fact the
              // client needs. It used to be conditional on deductible_in_motion
              // being truthy, and the columns are TEXT, so a stored '0' passed
              // the test and printed as a real $0 deductible.
              deductibleLine(o),
              o.term_months ? `${o.term_months} mo` : null,
              o.selected ? 'client-selected' : null,
            ].filter(Boolean).join(' · ')}
          </p>
        ) : null}
      </div>
      {/* One option is not a choice — the radio only appears when there is
          something to choose between. Clicking the marked one clears it. */}
      {showRecommend ? (
        <label className={`flex items-center gap-1.5 text-[11px] ${isOn ? 'text-text' : 'text-text-dim'}`}>
          <input
            type="radio"
            name="recommended"
            checked={isRec}
            disabled={!isOn}
            onChange={() => onRecommend(key)}
            onClick={() => { if (isRec) onRecommend(null); }}
            className="h-3.5 w-3.5 accent-accent"
          />
          Recommended
        </label>
      ) : null}
      </div>
      {missing !== READY ? <PresentMissing items={missing} q={q} o={o} onOpen={onOpenItem} /> : null}
    </div>
  );
}

export default function PresentQuotesSheet({ dealId, open, onOpenChange, onPresented, onReopened }) {
  const [quotes, setQuotes] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [checked, setChecked] = useState({});       // key `${quoteId}` or `${quoteId}:${optionId}` → bool
  const [recommendedKey, setRecommendedKey] = useState(null);
  const [why, setWhy] = useState('');
  // TWO DIFFERENT FACTS, TWO BOXES (prompt 109). `why` explains ONE option and
  // keeps its rule — validatePresentPayload still refuses one with nothing to
  // attach it to. `note` is context about the whole presentation and stands on
  // its own. Merging them is how the wrong one gets typed, which is this defect
  // one interaction earlier.
  const [note, setNote] = useState('');
  const [channels, setChannels] = useState({ email: true, sms: false });
  const [client, setClient] = useState(null);       // { email, phone, phone_source, name }
  const [typedPhone, setTypedPhone] = useState(''); // inline collect when there is none
  const [collecting, setCollecting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [clearing, setClearing] = useState(null); // quote id whose referral is being cleared
  const [blocked, setBlocked] = useState({}); // quote id -> the server's refusal, rendered on that card
  const [refused, setRefused] = useState({}); // quote id -> the gate's 422 list, rendered on that card
  const [checkError, setCheckError] = useState(''); // present_check_error: the check did not run, nothing is ready
  const [door, setDoor] = useState(null); // the sheet an item opened: { kind: 'wizard' | 'envelope', ... }

  // Re-read the server's list without touching what he has checked: after a
  // fix through an item's door, or a refusal.
  const refresh = useCallback(() => {
    if (!dealId) return;
    getPresentOptions(dealId)
      .then((d) => { setQuotes(d.quotes || []); setCheckError(d.present_check_error || ''); setRefused({}); })
      .catch((err) => toast.error(err instanceof ApiError ? (err.body?.error || err.code) : (err.message || 'Could not re-check the quotes')));
  }, [dealId]);

  useEffect(() => {
    if (!open || !dealId) return;
    setQuotes(null);
    setLoadError('');
    setWhy('');
    setRecommendedKey(null);
    setClient(null);
    setTypedPhone('');
    setCollecting(false);
    setCopied(false);
    setClearing(null);
    setBlocked({});
    setRefused({});
    setCheckError('');
    setDoor(null);
    setChannels({ email: true, sms: false });
    getPresentOptions(dealId)
      .then((d) => {
        const qs = d.quotes || [];
        setQuotes(qs);
        setCheckError(d.present_check_error || '');
        const c = d.client || { email: '', phone: '', phone_source: null, name: '' };
        setClient(c);
        setChannels({ email: !!c.email, sms: false });
        // Prefill from what the CLIENT SEES (item 23): an option is checked
        // when its quote is client_visible and the option is the one presented.
        // A withdrawn quote keeps its option flags (the withdraw doors clear
        // client_visible only), and re-checking it would re-present it.
        const pre = {};
        let rec = null;
        for (const q of qs) {
          if (q.options.length) {
            for (const o of q.options) {
              if (q.client_visible && o.presented) pre[`${q.id}:${o.id}`] = true;
              if (o.broker_recommended) { rec = `${q.id}:${o.id}`; if (o.recommended_reason) setWhy(o.recommended_reason); }
            }
          } else if (q.client_visible) {
            pre[q.id] = true;
            if (q.broker_recommended) rec = q.id;
          }
        }
        setChecked(pre);
        setRecommendedKey(rec);
      })
      .catch((err) => setLoadError(err instanceof ApiError ? (err.body?.error || err.code) : err.message));
  }, [open, dealId]);

  // Grouped and labelled from the CARRIER RECORD, not from the quotes.carrier
  // text. That column is not a display vocabulary: this one deal holds
  // 'global_aerospace' and 'iat' next to 'Starr Insurance' and 'US Specialty
  // Insurance Company', so the sheet was printing slugs at the client-facing
  // step. The server resolves carrier_display through carriers.name via
  // carrier_id (populated on every row here); grouping keys on carrier_id so
  // two spellings of one carrier cannot split into two headers.
  const byCarrier = useMemo(() => {
    const groups = new Map();
    for (const q of quotes || []) {
      const key = q.carrier_id != null ? `id:${q.carrier_id}` : `raw:${q.carrier || ''}`;
      if (!groups.has(key)) {
        groups.set(key, { label: q.carrier_display || q.carrier || 'Unknown carrier', qs: [] });
      }
      groups.get(key).qs.push(q);
    }
    return [...groups.entries()];
  }, [quotes]);

  const towerUrl = client?.tower_url || '';
  const copyTowerUrl = useCallback(() => {
    if (!towerUrl) return;
    navigator.clipboard.writeText(towerUrl)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => toast.error('Could not copy the Tower link'));
  }, [towerUrl]);

  // Total selectable rows across every carrier. 1 = nothing to choose between,
  // so no recommend radio and no "which one" question at all.
  const rowCount = (quotes || []).reduce((n, q) => n + (q.options.length || 1), 0);
  const showRecommend = rowCount > 1;

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const recommendedChecked = !!(recommendedKey && checked[recommendedKey]);
  // The SET is the only requirement. Recommendation and why are optional.
  // A TYPED NOTE THAT CANNOT BE CARRIED BLOCKS THE SEND (prompt 107).
  //
  // The field says "goes on the record" and it did not. `why` is only put in the
  // request body when an option is marked recommended, so a note written without
  // one never left the browser — three quotes reached Samuel Sheffield with a
  // note David had written and nobody received, and all three options carry
  // recommended_reason = null.
  //
  // The server would have refused it: validatePresentPayload returns "A
  // recommendation note needs the option it belongs to". That refusal was
  // unreachable from the one place that drops the note, so the only mechanism
  // that could have told him was behind the thing that made it necessary.
  //
  // THIS DOES NOT DECIDE WHERE A STANDALONE NOTE SHOULD LIVE — that is David's
  // call and it is in the report. It stops the silent loss tonight: a note he
  // can see is a note he can act on, and an empty field after a send now means
  // the note went.
  // Still true and still worth saying, but it is no longer a silent LOSS: the
  // note box below carries anything that is not about one option, so the fix
  // for this warning is now "move it down there" rather than "give up".
  const noteOrphaned = why.trim().length > 0 && !recommendedChecked;
  // IT REPORTS, IT DOES NOT GATE. Blocking the send on an unattachable note was
  // my first attempt and two existing guards refused it — "the why must not gate
  // the present" (present-flow) and "presenting must not become conditional on
  // completeness" (quote-wizard). They are right: the quotes are ready and the
  // client should get them; it is the NOTE that cannot travel, and the answer to
  // that is to say so, loudly, before and after, not to withhold three quotes.
  //
  // DECISION 13: Present is greyed while any checked row carries the server's
  // missing list (or its check failed), with the list beside the row. The
  // why and the note still gate nothing.
  const notReadyChecked = (quotes || []).some((q) => (q.options.length
    ? q.options.some((o) => checked[`${q.id}:${o.id}`] && rowMissing(q, o) !== READY)
    : checked[q.id] && rowMissing(q, null) !== READY));
  const canPresent = !busy && checkedCount > 0 && !checkError && !notReadyChecked;

  // Where an item opens. The routing is lib/quoteCompleteness missingItemDoor;
  // an envelope sheet needs the quote with its lines, read fresh.
  async function openItem(q, o, item) {
    const d = missingItemDoor(item, { policyType: q.policy_type || null });
    // A refusal card's item has no row of its own: it names its option.
    const opt = o || (item.option_id ? (q.options || []).find((x) => String(x.id) === String(item.option_id)) || null : null);
    if (d.kind === 'roster') { setDoor({ kind: 'roster' }); return; }
    if (d.kind === 'wizard') { setDoor({ kind: 'wizard', quoteId: q.id, optionId: opt?.id || null, step: d.step, field: d.field, items: rowMissing(q, opt) || [] }); return; }
    if (d.kind === 'envelope') {
      try { const r = await getQuoteFull(q.id); setDoor({ kind: 'envelope', sheet: d.sheet, quote: r?.quote || null }); }
      catch (err) { toast.error(err instanceof ApiError ? (err.body?.error || err.code) : (err.message || 'Could not open the quote')); }
    }
  }
  function closeDoor() { setDoor(null); refresh(); }

  // Channel availability: no address, no button. The server also resolves the
  // phone from CRM, so client.phone already reflects the "find" step.
  const hasEmail = !!client?.email;
  const knownPhone = client?.phone || '';
  const outboundPhone = knownPhone || (phoneIsSendable(typedPhone) ? typedPhone.trim() : '');
  const canText = !!outboundPhone;

  function toggle(key, on) {
    setChecked((c) => ({ ...c, [key]: on }));
    if (!on && recommendedKey === key) setRecommendedKey(null);
  }

  // The card updates from what the server READ BACK, not from the click. The
  // route re-reads the row after the write and answers referred:false only
  // when it saw it; anything else leaves the notice in place.
  async function clearReferral(quoteId, basis) {
    setClearing(quoteId);
    try {
      const res = await clearQuoteReferral(quoteId, basis);
      if (res?.referred !== false) throw new Error('The flag did not clear.');
      setQuotes((qs) => (qs || []).map((q) => (q.id === quoteId ? { ...q, referred: false } : q)));
      setBlocked((b) => { const n = { ...b }; delete n[quoteId]; return n; });
      toast.success(basis === 'firmed' ? 'Terms confirmed. It can go to the client now.' : 'Referred flag cleared.');
      onPresented?.();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Could not clear the referral');
    } finally {
      setClearing(null);
    }
  }

  async function handlePresent() {
    setBusy(true);
    try {
      const selections = [];
      for (const q of quotes || []) {
        if (q.options.length) {
          const optionIds = q.options.filter((o) => checked[`${q.id}:${o.id}`]).map((o) => o.id);
          if (optionIds.length) selections.push({ quote_id: q.id, option_ids: optionIds });
        } else if (checked[q.id]) {
          selections.push({ quote_id: q.id, option_ids: [] });
        }
      }
      // Optional: only send a recommendation when one is marked AND still
      // checked. The why rides along when written, never on its own.
      let recommended = null;
      if (recommendedChecked) {
        const [recQuoteId, recOptionId] = String(recommendedKey).split(':');
        recommended = { quote_id: recQuoteId, option_id: recOptionId || null, why: why.trim() || null };
      }
      const sendSms = !!channels.sms && canText;
      const res = await presentDealQuotes(dealId, {
        selections,
        recommended,
        note: note.trim() || null,
        channels: {
          email: !!channels.email && hasEmail,
          sms: sendSms,
          // Only when it came from this sheet — the server backfills it onto
          // the session and the CRM contact after the send.
          ...(sendSms && !knownPhone ? { sms_phone: outboundPhone } : {}),
        },
      });
      const bits = [`${res.option_count} option${res.option_count === 1 ? '' : 's'} presented`];
      if (res.channels?.email) bits.push(res.channels.email.sent ? `email sent to ${res.channels.email.to}` : `email failed: ${res.channels.email.error}`);
      if (res.channels?.sms) bits.push(res.channels.sms.sent ? `text sent to ${res.channels.sms.to}` : `text failed: ${res.channels.sms.error}`);
      const anyChannelFailed = (res.channels?.email && !res.channels.email.sent) || (res.channels?.sms && !res.channels.sms.sent);
      (anyChannelFailed ? toast.error : toast.success)(bits.join(' · '));
      toastSmsWarnings(res?.channels?.sms?.warnings);
      // THE NOTE DID NOT GO, AND HE HEARS IT NOW rather than discovering it.
      // Samuel Sheffield got three quotes and no note, and the only signal was
      // the field emptying — which looks identical to success. The sheet is left
      // OPEN so what he typed is still on screen to copy, because a note that is
      // gone from the form as well is gone twice.
      if (noteOrphaned) {
        toast.error('The reason you typed was NOT sent: it needs a recommended option. The note-to-the-client box goes out on its own.', { duration: Infinity });
      }
      onPresented?.();
      if (!anyChannelFailed && !noteOrphaned) onOpenChange?.(false);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      // A referred refusal lands on the card it names. It can only happen here
      // from a stale sheet (the box is held while the flag is set), so the
      // quote is re-marked referred on the spot rather than left tickable.
      if (body?.code === 'PRESENT_REFERRED' && body.quote_id) {
        setBlocked((b) => ({ ...b, [body.quote_id]: 'Presenting was refused.' }));
        setQuotes((qs) => (qs || []).map((q) => (q.id === body.quote_id ? { ...q, referred: true } : q)));
      }
      // THE GATE'S REFUSAL LANDS ON THE CARD IT NAMES (decision 13), the same
      // way the referred one does, and the list is re-read: nothing was shown.
      if (body?.code === 'QUOTE_NOT_PRESENTABLE' && body.quote_id) {
        setRefused((r) => ({ ...r, [body.quote_id]: Array.isArray(body.missing) ? body.missing : [] }));
        refresh();
      }
      toast.error(body?.error || err.message || 'Present failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="mx-auto max-h-[94vh] overflow-y-auto md:max-w-3xl">
        <SheetHeader>
          <SheetTitle>Present quotes to the client</SheetTitle>
          <SheetDescription>
            Check what the client sees. Unchecked options stay captured and fold into
            "other options we obtained" on the Tower page.
            {showRecommend ? ' Marking a recommendation is optional.' : ''}
          </SheetDescription>
        </SheetHeader>
        {/* A deal that ended while quoting: the client's link shows the ended page, so say it here, before anything is sent (David 2026-11-04). */}
        {dealId ? <DealReopenKey dealId={dealId} onReopened={onReopened} bannerOnly /> : null}

        {loadError ? (
          <p className="mt-6 text-sm text-danger">{loadError}</p>
        ) : quotes === null ? (
          <p className="mt-6 text-sm text-text-muted">Loading active quotes…</p>
        ) : quotes.length === 0 ? (
          <p className="mt-6 rounded border border-dashed border-border p-4 text-sm text-text-muted">
            No active quotes on this deal yet. Approve a parse or log one, then present.
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            {checkError ? (
              <p className="rounded border border-danger/50 bg-danger-soft px-3 py-2 text-[12px] text-danger">{checkError} Nothing can be presented until the check runs.</p>
            ) : null}
            {byCarrier.map(([key, { label, qs }]) => (
              <div key={key}>
                <p className="mb-1.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-text-dim">
                  {label}
                  {qs.some((q) => q.round > 1) ? <Badge variant="outline">round {Math.max(...qs.map((q) => q.round || 1))}</Badge> : null}
                </p>
                <div className="space-y-1.5">
                  {qs.map((q) => (
                    <div key={q.id} className="space-y-1.5">
                      {q.referred ? <ReferredNotice q={q} clearing={clearing} blocked={blocked[q.id]} onClear={clearReferral} /> : null}
                      {refused[q.id] ? (
                        <PresentMissing items={refused[q.id]} lead="Presenting was refused. Missing:" q={q} o={null} onOpen={openItem} />
                      ) : null}
                      {/* WHAT THE CLIENT SEES, keyed on client_visible (item 23),
                          never on the broker-side presenting marker. A visible
                          quote with no option flagged presented is in front of
                          the client today and is withdrawn by a present that
                          leaves it unchecked. */}
                      {q.client_visible && q.options.length && !q.options.some((o) => o.presented) ? (
                        <p className="pl-1 text-[11px] text-text-muted" data-visible-unflagged>
                          The client sees this quote now. Check its option to keep it in front of them; unchecked, it is withdrawn when you present.
                        </p>
                      ) : null}
                      {q.options.length
                        ? q.options.map((o) => (
                            <OptionRow key={o.id} q={q} o={o} checked={checked} onToggle={toggle} recommendedKey={recommendedKey} onRecommend={setRecommendedKey} showRecommend={showRecommend} held={!!q.referred} missing={rowMissing(q, o)} onOpenItem={openItem} />
                          ))
                        : <OptionRow key={q.id} q={q} o={null} checked={checked} onToggle={toggle} recommendedKey={recommendedKey} onRecommend={setRecommendedKey} showRecommend={showRecommend} held={!!q.referred} missing={rowMissing(q, null)} onOpenItem={openItem} />}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* TWO BOXES, TWO FACTS. The reason belongs to one option; the
                note belongs to the presentation. They are visibly different
                because if they are not, the wrong one gets typed — which is
                this whole defect, one interaction earlier. */}
            {showRecommend ? (
            <div>
              <p className="mb-0.5 text-[10.5px] font-medium uppercase tracking-wide text-text-dim">
                {/* ONE MEANING PER BOX. This label used to change identity
                    depending on whether a recommendation was marked — the same
                    control was "why you recommend it" or "note to the client"
                    — so the field David typed into meant different things at
                    different moments and only one of them was carried. It is
                    the option's reason now, always, and the standalone note has
                    its own box below. */}
                Why you recommend this option (optional)
              </p>
              {/* A textarea, not an input. This is the advisory record and it
                  goes in front of the client, so the field has to show what he
                  typed: a live note read '...You can't make a wrong choice tho'
                  on screen while the column held the whole sentence. Nothing was
                  ever lost, but he could not see what the client would. */}
              <textarea
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                rows={3}
                placeholder="e.g. best hull terms for the money and no training requirement"
                className="w-full resize-y rounded border border-border bg-bg px-3 py-2 text-sm text-text"
              />
              {/* LOUD, AND IT RENDERS WHETHER OR NOT THE RECOMMEND CONTROL IS
                  SHOWING. It used to be 11px grey text gated on `showRecommend`,
                  which is false on a single-option present — so the one case
                  with no visible hint at all was also a case where the note was
                  silently dropped. */}
              {noteOrphaned ? (
                <p className="mt-1 rounded border border-warning/50 bg-warning-soft px-2 py-1.5 text-[11.5px] text-warning">
                  A reason belongs to a recommended option. Mark one above, or move this
                  into the note to the client below, which goes out on its own.
                </p>
              ) : null}
            </div>
            ) : null}

            {/* THE STANDALONE NOTE. David ruled it is a real fact and the field's
                own placeholder always said so: "same terms and premium as the
                expiring policy" is context about the whole presentation, not a
                reason for preferring one option. It reaches the Tower page and
                the email; the text message points at it rather than carrying it
                (see present-quotes.service for why). */}
            <div>
              <p className="mb-0.5 text-[10.5px] font-medium uppercase tracking-wide text-text-dim">
                Note to the client (optional)
              </p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="e.g. same terms and premium as the expiring policy"
                className="w-full resize-y rounded border border-border bg-bg px-3 py-2 text-sm text-text"
              />
              <p className="mt-1 text-[11px] text-text-dim">
                Goes on the Tower page and in the email. The text message says a note is there
                and links to it.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-text">
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-text-dim">Notify via</span>
              {hasEmail ? (
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={channels.email} onChange={(e) => setChannels((c) => ({ ...c, email: e.target.checked }))} className="h-4 w-4 accent-accent" />
                  Email <span className="text-[11px] text-text-dim">{client.email}</span>
                </label>
              ) : (
                <span className="text-[11px] text-text-dim">No email on file, so no email</span>
              )}

              {/* Text: offered only when there IS a number. Found (session or
                  CRM contact) or typed here — never a button that fails. */}
              {canText ? (
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={channels.sms} onChange={(e) => setChannels((c) => ({ ...c, sms: e.target.checked }))} className="h-4 w-4 accent-accent" />
                  Text <span className="text-[11px] text-text-dim">{fmtPhone(outboundPhone)}{client?.phone_source === 'crm_contact' ? ' (from CRM)' : ''}</span>
                </label>
              ) : collecting ? (
                <span className="flex items-center gap-1.5">
                  <input
                    value={typedPhone}
                    onChange={(e) => setTypedPhone(e.target.value)}
                    placeholder="(706) 555-0143"
                    inputMode="tel"
                    autoFocus
                    className="w-40 rounded border border-border bg-bg px-2 py-1 text-[13px] text-text"
                  />
                  <span className="text-[11px] text-text-dim">
                    {typedPhone.trim() && !phoneIsSendable(typedPhone) ? 'Needs 10 digits' : 'Saved to the client on send'}
                  </span>
                </span>
              ) : (
                <button type="button" onClick={() => setCollecting(true)} className="text-[12px] text-accent underline underline-offset-2">
                  No mobile on file. Add one to text.
                </button>
              )}
              <span className="text-[11px] text-text-dim">Neither = publish to Tower silently</span>
            </div>

            {/* The link the client opens, grabbable without leaving the sheet
                (David presents with a text thread already open). It is a READ:
                the session was already loaded to work out who is reachable, so
                the token came along with it. A deal with no session has no
                token and no Tower page yet, so it gets the sentence instead of
                a button that would copy a dead URL. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-text-dim">Tower link</span>
              {towerUrl ? (
                <>
                  <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-text-muted">{towerUrl}</span>
                  <button
                    type="button"
                    onClick={copyTowerUrl}
                    className="shrink-0 rounded-[5px] border border-border px-2.5 py-1 text-[12px] hover:border-border-strong hover:bg-surface-strong"
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </>
              ) : (
                <span className="text-[11px] text-text-dim">Created when you present.</span>
              )}
            </div>

            <Button onClick={handlePresent} disabled={!canPresent} className="w-full">
              {busy ? 'Presenting…' : `Present ${checkedCount} option${checkedCount === 1 ? '' : 's'}${(channels.email && hasEmail) || (channels.sms && canText) ? ' and notify' : ' to Tower'}`}
            </Button>
            {checkedCount === 0 ? (
              <p className="text-[11.5px] text-warning">Check at least one option for the client to see.</p>
            ) : checkError ? (
              <p className="text-[11.5px] text-danger" data-present-check-error>{checkError}</p>
            ) : notReadyChecked ? (
              <p className="text-[11.5px] text-warning" data-present-not-ready>Every checked quote needs the items listed beside it.</p>
            ) : null}
          </div>
        )}
      </SheetContent>
      {/* EACH ITEM OPENS ITS FIELD (decision 13). The wizard opens on the
          item's step and option; an envelope quote opens its own log sheet; a
          pilot off the deal's list opens the deal roster. Closing any re-reads the server's list, so the list beside each row
          is always the gate's, never this sheet's guess. */}
      {door?.kind === 'wizard' ? (
        <QuoteWizardSheet dealId={dealId} quoteId={door.quoteId} open
          onOpenChange={(o) => { if (!o) closeDoor(); }}
          initialStep={door.step} focusField={door.field} initialOptionId={door.optionId} presentMissing={door.items} />
      ) : null}
      {door?.kind === 'envelope' && door.sheet === 'hangar' ? (
        <LogHangarQuoteSheet dealId={dealId} quote={door.quote} open onOpenChange={(o) => { if (!o) closeDoor(); }} />
      ) : null}
      {door?.kind === 'envelope' && door.sheet === 'gl' ? (
        <LogGlQuoteSheet dealId={dealId} quote={door.quote} open onOpenChange={(o) => { if (!o) closeDoor(); }} />
      ) : null}
      {door?.kind === 'roster' ? (
        <Suspense fallback={null}>
          <RosterSheet dealId={dealId} open onOpenChange={(o) => { if (!o) closeDoor(); }}
            onLprRequired={() => { closeDoor(); toast.error('Finish the release from the deal actions, under Roster.'); }} />
        </Suspense>
      ) : null}
    </Sheet>
  );
}
